import { useCallback } from 'react';
import {
  rankWith,
  ControlProps,
  isControl,
  and,
  schemaMatches,
} from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { TreeSelectPicker } from '../TreeSelectPicker';

/**
 * Derive the API path from a hierarchy key.
 * "Inventory.ProductGroup" -> "/api/inventory/inventory-product-groups"
 */
function hierarchyKeyToApiPath(hierarchyKey: string): string {
  const module = hierarchyKey.split('.')[0];
  const entityName = hierarchyKey.replace(/\./g, '');
  const kebab = entityName
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '');
  const plural = kebab.endsWith('y')
    ? kebab.slice(0, -1) + 'ies'
    : kebab.endsWith('s') || kebab.endsWith('x') || kebab.endsWith('z')
      || kebab.endsWith('ch') || kebab.endsWith('sh')
      ? kebab + 'es'
      : kebab + 's';
  return `/api/${module.toLowerCase()}/${plural}`;
}

/**
 * Tester: matches fields with format 'hierarchy-ref'
 */
export const hierarchyRefRendererTester = rankWith(
  10,
  and(
    isControl,
    schemaMatches((schema) => schema.format === 'hierarchy-ref'),
  )
);

/**
 * HierarchyRef renderer component — renders a TreeSelectPicker
 * for selecting a node from a hierarchy tree.
 */
const HierarchyRefRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, label, required, errors, schema, visible, enabled } = props;

  if (!visible) return null;

  const hierarchyKey = (schema as any)?.['x-hierarchy']?.hierarchyKey;
  if (!hierarchyKey) return null;

  const apiPath = hierarchyKeyToApiPath(hierarchyKey);

  const onChange = useCallback(
    (nodeId: string | null) => {
      handleChange(path, nodeId || undefined);
    },
    [handleChange, path],
  );

  return (
    <TreeSelectPicker
      hierarchyApiPath={apiPath}
      value={data || null}
      onChange={onChange}
      label={label || 'Select'}
      disabled={!enabled}
      required={required}
      error={errors}
      size="small"
      showPath
    />
  );
};

export const HierarchyRefRenderer = withJsonFormsControlProps(HierarchyRefRendererComponent);

export const hierarchyRefRendererEntry = {
  tester: hierarchyRefRendererTester,
  renderer: HierarchyRefRenderer,
};
